import React, { useState } from 'react';
import { MasterDocument } from '@util/firestore/master';

interface ProductDescriptionProps {
  master: MasterDocument;
}

const READ_MORE_THRESHOLD = 100;

/** Renders inline markdown bold (**text**) as <strong> elements. */
function renderInlineBold(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

/**
 * Splits a long text into paragraphs. If the text already has newlines, split
 * on those. Otherwise, split every ~3 sentences so long descriptions are readable.
 *
 * Uses ". [A-Z]" as the sentence boundary to avoid false splits on decimal
 * numbers (e.g. "45.0MP", "29.97fps") and abbreviations common in camera specs.
 */
function splitIntoParagraphs(text: string): string[] {
  // If the text already has paragraph breaks, use them
  const byNewline = text.split(/\n+/).filter(p => p.trim().length > 0);
  if (byNewline.length > 1) return byNewline;

  // Split on ". " followed by an uppercase letter (real sentence boundary)
  // This avoids splitting on decimals like "45.0" or "f/2.8"
  const sentences = text.split(/(?<=\.)\s+(?=[A-Z])/);
  if (sentences.length <= 3) return [text];

  const paragraphs: string[] = [];
  for (let i = 0; i < sentences.length; i += 3) {
    paragraphs.push(sentences.slice(i, i + 3).join(' ').trim());
  }
  return paragraphs;
}

export default function ProductDescription({ master }: ProductDescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const [prosExpanded, setProsExpanded] = useState(false);
  const [consExpanded, setConsExpanded] = useState(false);

  const description = master.gmDescription;
  const pros = master.gmPros;
  const cons = master.gmCons;

  const hasDescription = !!description;
  const hasPros = Array.isArray(pros) && pros.length > 0;
  const hasCons = Array.isArray(cons) && cons.length > 0;

  if (!hasDescription && !hasPros && !hasCons) {
    return null;
  }

  const showReadMore = hasDescription && description.length > READ_MORE_THRESHOLD;

  return (
    <div>
      <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[1.5rem]">
        Description<span className="sr-only"> — {master.brand} {master.model}</span>
      </h2>
      {hasDescription && (
        <div>
          <div
            className={`text-[1.5rem] leading-[2.4rem] text-brand-dark-gray${
              showReadMore && !expanded ? ' line-clamp-4' : ''
            }`}
          >
            {splitIntoParagraphs(description).map((para, i) => (
              <p key={i} className={i > 0 ? 'mt-[1rem]' : ''}>
                {renderInlineBold(para)}
              </p>
            ))}
          </div>
          {showReadMore && (
            <button
              type="button"
              className="text-brand-orange font-medium mt-[0.8rem] cursor-pointer hover:underline text-[1.4rem]"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>
      )}

      {hasPros && (
        <div>
          <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mt-[2rem] mb-[1rem]">
            Pros<span className="sr-only"> — {master.brand} {master.model}</span>
          </h2>
          <ul role="list" aria-label="Pros" className="space-y-[0.6rem]">
            {pros.map((pro, index) => (
              <li
                key={index}
                role="listitem"
                className={`flex items-start gap-[0.8rem] text-[1.5rem] text-gray-600${
                  !prosExpanded && index >= 3 ? ' hidden' : ''
                }`}
              >
                <span className="text-green-600 mt-[0.2rem] shrink-0">✓</span>
                <span>{pro}</span>
              </li>
            ))}
          </ul>
          {pros.length > 3 && (
            <button
              type="button"
              className="text-brand-orange font-medium mt-[0.8rem] cursor-pointer hover:underline text-[1.4rem]"
              onClick={() => setProsExpanded((prev) => !prev)}
            >
              {prosExpanded ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>
      )}

      {hasCons && (
        <div>
          <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mt-[2rem] mb-[1rem]">
            Cons<span className="sr-only"> — {master.brand} {master.model}</span>
          </h2>
          <ul role="list" aria-label="Cons" className="space-y-[0.6rem]">
            {cons.map((con, index) => (
              <li
                key={index}
                role="listitem"
                className={`flex items-start gap-[0.8rem] text-[1.5rem] text-gray-600${
                  !consExpanded && index >= 3 ? ' hidden' : ''
                }`}
              >
                <span className="text-red-500 mt-[0.2rem] shrink-0">✗</span>
                <span>{con}</span>
              </li>
            ))}
          </ul>
          {cons.length > 3 && (
            <button
              type="button"
              className="text-brand-orange font-medium mt-[0.8rem] cursor-pointer hover:underline text-[1.4rem]"
              onClick={() => setConsExpanded((prev) => !prev)}
            >
              {consExpanded ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
